import React, { type FC, useRef } from 'react';

import {
  Button,
  Checkbox,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import {
  DataList,
  DataListCell,
  DataListEmptyState,
  DataListErrorState,
  DataListLoadingState,
  DataListRow,
  DataListTextHeader,
} from '@/components/DataList';
import { CandidateSortColumn } from '@/services/api';

import { SortFilter } from '../../components/Filters/Sort/SortFilter';
import { IconAlertCircle, IconEllipseShade } from '../../components/Icons';
import { CandidateListItems } from '../candidate/CandidateListItems';
import { type CandidateListProps } from '../candidate/types';

export const SurveyParticipantsList: React.FC<CandidateListProps> = ({
  candidatesQuery,
  selectedCandidates,
  onCandidateSelect,
  onSelectAll,
  sortOrder,
  setSortOrder,
  sortItem,
  setSortItem,
}) => {
  const { t } = useTranslation([
    'common',
    'survey',
    'auth',
    'candidate',
    'components',
  ]);
  const deleteCandidateModalDisclosure = useDisclosure();

  const {
    data: candidatesPaged,
    isLoading,
    isError,
    refetch,
  } = candidatesQuery;

  const candidates = candidatesPaged?.data ?? [];

  const selectAll =
    candidates.length > 0 && selectedCandidates.length === candidates.length;

  const noSelection = selectedCandidates.length === 0;

  const deleteModalFinalFocusRef = useRef(null);

  return (
    <>
      <DataList>
        <DataListRow>
          <DataListCell w={10} alignItems="center" p={0} borderRight="none">
            <Checkbox
              isChecked={selectAll}
              onChange={(e) => onSelectAll(e.target.checked)}
            />
          </DataListCell>
          {noSelection ? (
            <>
              <DataListCell px={3} flex={2}>
                <DataListTextHeader>
                  {t('common:information.personalInformation')}
                </DataListTextHeader>
              </DataListCell>
              <DataListCell
                flexDir="row"
                align="center"
                justifyContent="flex-start"
                gap={2}
              >
                <DataListTextHeader>
                  {t('common:timeAndDate.uploadDate')}
                </DataListTextHeader>

                <SortFilter<CandidateSortColumn>
                  onSortOrderChange={(newSortOrder, sortColumnAffected) => {
                    setSortOrder(newSortOrder);
                    setSortItem(sortColumnAffected);
                  }}
                  currentSortOrder={sortOrder}
                  activeSortColumn={sortItem}
                  thisFiltersSortColumn={CandidateSortColumn.CreatedAt}
                />
              </DataListCell>
              <DataListCell align="center">
                <DataListTextHeader>
                  {t('common:detail.status')}
                </DataListTextHeader>
              </DataListCell>
              <DataListCell flexDir="row" align="center" gap={2}>
                <DataListTextHeader>
                  {t('common:detail.rating')}
                </DataListTextHeader>

                <SortFilter<CandidateSortColumn>
                  onSortOrderChange={(newSortOrder, sortColumnAffected) => {
                    setSortOrder(newSortOrder);
                    setSortItem(sortColumnAffected);
                  }}
                  currentSortOrder={sortOrder}
                  activeSortColumn={sortItem}
                  thisFiltersSortColumn={CandidateSortColumn.Rating}
                />
              </DataListCell>
              <DataListCell flexDir="row" align="center" gap={2} w="12%">
                <DataListTextHeader>{t('survey:point')}</DataListTextHeader>

                <SortFilter<CandidateSortColumn>
                  onSortOrderChange={(newSortOrder, sortColumnAffected) => {
                    setSortOrder(newSortOrder);
                    setSortItem(sortColumnAffected);
                  }}
                  currentSortOrder={sortOrder}
                  activeSortColumn={sortItem}
                  thisFiltersSortColumn={CandidateSortColumn.AiRating}
                />
              </DataListCell>
              <DataListCell w="18%">
                <DataListTextHeader>
                  {t('survey:transactions', { defaultValue: 'Transactions' })}
                </DataListTextHeader>
              </DataListCell>
            </>
          ) : (
            <DataListCell px={2}>
              <HStack>
                <Text fontSize="sm" color="gray.600">
                  {selectedCandidates.length}{' '}
                  {t('survey:participantsSelected', {
                    defaultValue: 'participants selected',
                  })}
                </Text>
              </HStack>
            </DataListCell>
          )}
        </DataListRow>
        {isLoading && <DataListLoadingState />}
        {isError && (
          <DataListErrorState
            title={t('auth:users.feedbacks.loadingUserError.title')}
            retry={refetch}
          />
        )}
        {!isLoading && !isError && candidates.length === 0 && (
          <DataListEmptyState />
        )}
        <Flex
          flexDirection="column"
          position="relative"
          boxShadow="none"
          borderRadius="none"
          h="620px"
          bg="white"
          overflow="auto"
        >
          {candidates.map((candidate, index) => {
            return (
              <CandidateListItems
                variant="survey"
                isShowAiScore
                key={candidate.id ?? Math.random().toString()}
                candidate={candidate}
                isSelected={Boolean(
                  selectedCandidates.find((c) => c.id === candidate.id)
                )}
                onSelect={onCandidateSelect}
                participantIndex={index}
              />
            );
          })}
        </Flex>
      </DataList>

      <DeleteParticipantModal
        isOpen={deleteCandidateModalDisclosure.isOpen}
        onClose={deleteCandidateModalDisclosure.onClose}
        title={t('survey:deleteParticipantTitle', {
          defaultValue: 'Delete participants',
        })}
        subTitle={t('survey:deleteParticipantSubTitle', {
          defaultValue:
            'Are you sure you want to delete the selected participants?',
        })}
        isLoading={false}
        onDelete={async () => {
          // TODO: Implement delete participant mutation
          console.log('Delete participants');
        }}
        finalFocusRef={deleteModalFinalFocusRef}
      />
    </>
  );
};

export type DeleteModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subTitle: string;
  onDelete: () => Promise<void>;
  isLoading: boolean;
  finalFocusRef?: React.RefObject<HTMLButtonElement>;
};

const DeleteParticipantModal: FC<DeleteModalProps> = ({
  isOpen,
  onClose,
  title,
  subTitle,
  onDelete,
  isLoading,
  finalFocusRef,
}) => {
  const { t } = useTranslation(['common']);

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      finalFocusRef={finalFocusRef}
    >
      <ModalOverlay />
      <ModalContent maxWidth="440px" borderRadius="2xl" p={0}>
        <ModalCloseButton
          borderRadius="full"
          borderWidth={1}
          borderColor="gray.50"
          mt={2.5}
        />
        <ModalHeader justifyItems="center" px={0} mt={3}>
          <Stack px={10} mb={2}>
            <Flex justifyContent="center" position="relative" mt={3}>
              <IconEllipseShade fontSize={64} position="absolute" top={-6} />
              <Flex
                h={12}
                w={12}
                borderWidth="1px"
                borderColor="#EFD3B8"
                borderRadius="50%"
                justifyContent="center"
                alignItems="center"
              >
                <IconAlertCircle color="error.500" fontSize={28} />
              </Flex>
            </Flex>
          </Stack>
          <Text textAlign="center" fontSize="xl" px={12} fontWeight="semibold">
            {title}
          </Text>
        </ModalHeader>

        <ModalBody p={0}>
          <VStack gap={0}>
            <Text my={3} px={10} color="gray.600" textAlign="center">
              {subTitle}
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack gap={3} w="full">
            <Button onClick={onClose} flexGrow={1} color="brand.500">
              {t('actions.cancel2')}
            </Button>
            <Button
              isLoading={isLoading}
              flexGrow={1}
              variant="@primary"
              onClick={async () => {
                await onDelete();
                onClose();
              }}
            >
              {t('actions.yes_delete')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
